import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AppContext from '@/common/context/app.context'
import { parcelDirections, parcelTypesByDirection } from '@/parcels/constants'
import parcelServices from '@/parcels/services/services'
import returnsServices from '@/returns/services/services'

import { debounceMs, pageSize, settingsMap } from '../constants'
import { filterSettings, formatParcelResult, formatReturnResult, formatSettingResult } from '../helpers'
import { transformResponseParcels } from './transformers'

const initialCategoryState = { loading: false, results: [] }

const searchParcels = async ({ storeId, searchQuery, direction }) => {
  const response = await parcelServices.getParcels({
    storeId,
    params: { search: searchQuery, page_size: pageSize, type: parcelTypesByDirection[direction] },
  })

  return transformResponseParcels(response.data).map(formatParcelResult)
}

const useGlobalSearch = ({ show }) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const [query, setQuery] = useState('')
  const [shipments, setShipments] = useState(initialCategoryState)
  const [returns, setReturns] = useState(initialCategoryState)
  const [settings, setSettings] = useState(initialCategoryState)
  const [activeIndex, setActiveIndex] = useState(0)
  const debounceRef = useRef(null)

  const returnPortalId = info?.selectedReturnPortal?.id
  const storeId = info?.selectedStore?.id
  const preferences = info?.selectedStore?.preferences
  const showOutbound = preferences?.allows_outbound || preferences?.allows_shipments

  const resetState = useCallback(() => {
    setQuery('')
    setShipments(initialCategoryState)
    setReturns(initialCategoryState)
    setSettings(initialCategoryState)
    setActiveIndex(0)
  }, [])

  useEffect(() => {
    if (!show) {
      resetState()
    }
  }, [show, resetState])

  const searchSettings = useCallback(
    searchQuery => {
      const filtered = filterSettings(settingsMap, searchQuery, t)
      setSettings({ loading: false, results: filtered.map(s => formatSettingResult(s, t)) })
    },
    [t]
  )

  const searchShipments = useCallback(
    async searchQuery => {
      if (!storeId) return
      setShipments(prev => ({ ...prev, loading: true }))
      try {
        const [outboundResults, inboundResults] = await Promise.all([
          showOutbound ? searchParcels({ storeId, searchQuery, direction: parcelDirections.outbound }) : [],
          searchParcels({ storeId, searchQuery, direction: parcelDirections.inbound }),
        ])

        setShipments({
          loading: false,
          results: [...outboundResults, ...inboundResults],
        })
      } catch {
        setShipments({ loading: false, results: [] })
      }
    },
    [storeId, showOutbound]
  )

  const searchReturns = useCallback(
    async searchQuery => {
      if (!returnPortalId) return
      setReturns(prev => ({ ...prev, loading: true }))
      try {
        const response = await returnsServices.getReturns({
          returnPortalId,
          params: { search: searchQuery, page_size: pageSize },
        })
        setReturns({
          loading: false,
          results: (response.data?.results || []).map(formatReturnResult),
        })
      } catch {
        setReturns({ loading: false, results: [] })
      }
    },
    [returnPortalId]
  )

  const handleQueryChange = useCallback(
    value => {
      setQuery(value)
      setActiveIndex(0)

      if (!value.trim()) {
        setShipments(initialCategoryState)
        setReturns(initialCategoryState)
        setSettings(initialCategoryState)
        return
      }

      searchSettings(value)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      debounceRef.current = setTimeout(() => {
        searchShipments(value)
        searchReturns(value)
      }, debounceMs)
    },
    [searchSettings, searchShipments, searchReturns]
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const allResults = [...settings.results, ...shipments.results, ...returns.results]
  const isLoading = shipments.loading || returns.loading
  const hasQuery = query.trim().length > 0
  const hasResults = allResults.length > 0

  return {
    query,
    handleQueryChange,
    shipments,
    returns,
    settings,
    allResults,
    activeIndex,
    setActiveIndex,
    isLoading,
    hasQuery,
    hasResults,
  }
}

export default useGlobalSearch
